/**
 * Doorbell share screen — generate a QR code visitors can scan.
 * Mario-style "code block" — original styling.
 */
import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { useApp } from '@/state/store';
import { visitsApi } from '@/lib/api';
import { sfx } from '@/lib/audio';
import { QRSprite, LoadingSprite, CheckSprite, ArrowSprite } from '@/components/Sprites';

export function DoorbellShareScreen() {
  const { community, user, goto, showToast } = useApp();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [url, setUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!community?.invite_code) {
      setError('Tu comunidad no tiene código de invitación todavía.');
      return;
    }
    // Never trust the runtime origin — a printed QR must always point to
    // production. Dev environments set window.location to localhost which
    // would otherwise leak into printed stickers.
    const origin = 'https://vicin.app';
    const visitUrl = visitsApi.visitUrl(community.invite_code, origin);
    setUrl(visitUrl);

    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, visitUrl, {
        width: 160,
        margin: 1,
        errorCorrectionLevel: 'Q',
        // High-contrast palette so all scanners (iOS Camera, Android Lens,
        // WhatsApp) read it reliably. The GameBoy bezel stays green; only
        // the QR module colors deviate to maximize scanner success.
        color: { dark: '#0f380f', light: '#f4f4f0' },
      }).catch((err) => {
        console.error('QR error', err);
        setError('No se pudo generar el código');
        sfx.error();
      });
    }
  }, [community?.invite_code]);

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      sfx.coin();
      setCopied(true);
      showToast('¡Copiado!', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast('No se pudo copiar', 'error');
      sfx.error();
    }
  };

  // A = copy, B = back
  const handlersRef = useRef({ copy, goto });
  handlersRef.current = { copy, goto };

  useEffect(() => {
    const onA = () => handlersRef.current.copy();
    const onB = () => handlersRef.current.goto('home');
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, []);

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <QRSprite size={11} />
          <span className="gb-pix gb-pix-xs">MI QR · {community?.name?.slice(0, 10) || 'VICIN'}</span>
        </div>
        <div className="gb-status-right">
          <span className="gb-pix gb-pix-xs">TIMBRE</span>
        </div>
      </div>

      <div className="gb-body">
        {error ? (
          <div className="gb-center">
            <div className="gb-pix gb-pix-sm" style={{ textAlign: 'center' }}>{error}</div>
          </div>
        ) : !url ? (
          <div className="gb-center">
            <LoadingSprite size={32} />
          </div>
        ) : (
          <div className="gb-stack" style={{ gap: 6, alignItems: 'center', marginTop: 2 }}>
            <div className="gb-pix gb-pix-sm" style={{ textAlign: 'center' }}>TIMBRE DIGITAL</div>
            <div className="gb-pix gb-pix-xs gb-mid-text" style={{ textAlign: 'center', fontSize: 5, lineHeight: 1.4 }}>
              Imprimí este QR y pegalo en la entrada. Los visitantes lo escanean para timbrarte.
            </div>

            <div
              style={{
                marginTop: 4,
                padding: 8,
                background: 'var(--gb-bg)',
                border: '2px solid var(--gb-dark)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                imageRendering: 'pixelated',
              }}
            >
              <canvas
                ref={canvasRef}
                style={{
                  width: 140,
                  height: 140,
                  imageRendering: 'pixelated',
                }}
                data-testid="doorbell-qr"
              />
            </div>

            <div className="gb-pix gb-pix-xs" style={{ fontSize: 5, color: 'var(--gb-mid)', textAlign: 'center', marginTop: 2 }}>
              {user?.unit?.code ? `PISO ${user.unit.code}` : ''}
            </div>

            <button
              onClick={copy}
              className="gb-action"
              data-testid="doorbell-copy"
              style={{ marginTop: 4, minWidth: 140 }}
            >
              {copied ? <><CheckSprite size={10} /> COPIADO</> : <>COPIAR URL <ArrowSprite size={10} dir="right" /></>}
            </button>
          </div>
        )}

        <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, textAlign: 'center', marginTop: 'auto', paddingTop: 4, display: 'none' }}>
          [A] COPIAR · [B] VOLVER
        </div>
      </div>
    </div>
  );
}
